import { Fragment, ReactElement, useMemo } from 'react';
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';

import {
  ifNumberAddCommasAndRound,
  ifNumberRoundToNDecimalPlaces,
} from '../../../../../utilities/numberFormatters';
import {
  customSortingFn,
  fuzzyFilter,
} from '../../../../tables/ultraTable/utils/ultratable.utils';

export type CounterpartyDetailData = {
  name: string;
  price: number;
  delta: number;
  size: number;
  riskFactor: number;
  localExposure: number;
  baseExposure: number;
  grossExposure: number;
  commitment: number;
};

function buildCounterpartyDetailTableData(data: any): CounterpartyDetailData[] {
  if (
    !data ||
    !data.assetInformation ||
    !Array.isArray(data.assetInformation)
  ) {
    return [];
  }

  return data.assetInformation.map((asset: any) => {
    // Needed (for backward compatibility)
    const parseNumber = (value: any): number => {
      if (typeof value === 'number') return value;
      if (typeof value === 'string') {
        const parsed = parseFloat(value.replace(/,/g, ''));
        return isNaN(parsed) ? 0 : parsed;
      }
      return 0;
    };

    return {
      name: asset.name || 'N/A',
      price:
        typeof asset.price === 'number'
          ? asset.price
          : parseNumber(asset.price),
      delta:
        typeof asset.delta === 'number'
          ? asset.delta
          : parseNumber(asset.delta),
      size:
        typeof asset.size === 'number' ? asset.size : parseNumber(asset.size),
      riskFactor:
        typeof asset.riskFactor === 'number'
          ? asset.riskFactor
          : parseNumber(asset.riskFactor),
      localExposure: parseNumber(asset.localExposure),
      baseExposure: parseNumber(asset.baseExposure),
      grossExposure: parseNumber(asset.grossExposure),
      commitment: parseNumber(asset.commitment),
    };
  });
}

const counterpartyDetailColumns: ColumnDef<CounterpartyDetailData>[] = [
  {
    accessorKey: 'name',
    header: () => 'Name',
  },
  {
    accessorKey: 'price',
    header: () => 'Price',
    cell: (props) => {
      const value = props.getValue() as number;
      return value >= 10
        ? ifNumberRoundToNDecimalPlaces(value, 2)
        : ifNumberRoundToNDecimalPlaces(value, 4);
    },
  },
  {
    accessorKey: 'delta',
    header: () => 'Delta',
    cell: (props) =>
      ifNumberRoundToNDecimalPlaces(props.getValue() as number, 3),
  },
  {
    accessorKey: 'size',
    header: () => 'Size',
    cell: (props) => ifNumberAddCommasAndRound(props.getValue()),
  },
  {
    accessorKey: 'riskFactor',
    header: () => 'Risk Factor',
    cell: (props) =>
      ifNumberRoundToNDecimalPlaces(props.getValue() as number, 2),
  },
  {
    accessorKey: 'localExposure',
    header: () => 'Local Exposure',
    cell: (props) => ifNumberAddCommasAndRound(props.getValue()),
  },
  {
    accessorKey: 'baseExposure',
    header: () => 'Base Exposure',
    cell: (props) => ifNumberAddCommasAndRound(props.getValue()),
  },
  {
    accessorKey: 'grossExposure',
    header: () => 'Gross Exposure',
    cell: (props) => ifNumberAddCommasAndRound(props.getValue()),
  },
  {
    accessorKey: 'commitment',
    header: () => 'Commitment',
    cell: (props) => ifNumberAddCommasAndRound(props.getValue()),
  },
];

export default function CounterpartyDetailTable(props: any): ReactElement {
  const data = useMemo(
    () => buildCounterpartyDetailTableData(props.data),
    [props.data],
  );

  const table = useReactTable<CounterpartyDetailData>({
    data,
    columns: counterpartyDetailColumns,
    filterFns: {
      fuzzy: fuzzyFilter,
    },
    sortingFns: {
      customSortingFn: customSortingFn,
    },
    getCoreRowModel: getCoreRowModel(),
  });

  return data.length ? (
    <div className="mt-[0.30rem] border-b-2 border-l-2 border-r-2 p-[0.31rem]">
      <table className="min-w-full table-fixed border border-[#274C77]">
        <thead className="sticky bg-[#F3F9FF]">
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id} className="flex py-2">
              {headerGroup.headers.map((header) => {
                return (
                  <th
                    key={header.id}
                    className="flex w-full flex-col items-center justify-between gap-5 px-2 py-1 text-base font-semibold text-mainblue"
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row) => {
            return (
              <Fragment key={row.id}>
                <tr className="flex border-t border-gray-300 py-2">
                  {row.getVisibleCells().map((cell) => {
                    return (
                      <td
                        key={cell.id}
                        className="flex w-full items-center justify-center px-2 py-1 text-center text-sm text-black"
                      >
                        {flexRender(
                          cell.column.columnDef.cell,
                          cell.getContext(),
                        )}
                      </td>
                    );
                  })}
                </tr>
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  ) : (
    <h2 className="mx-[1.88rem] my-5 text-[#274C77]">No records to display</h2>
  );
}
